import { LevelCatalog } from "@highmarch/app";
import type { LevelController } from "../level/levelController";
import { icon } from "./iconsKit";
import { closeBtnHtml } from "./kit";
import { Rarity } from "@highmarch/domain";
import { SkinSwitcher } from "./debug/skinSwitcher";

/** "+8 gear": this many items of each rarity from Common to Epic. */
const DEBUG_GEAR_PER_RARITY = 2;

export interface DebugActions {
  catalog: LevelCatalog;
  styleGuide: () => void;
  /** Captain Bram starts over: the guide's save is cleared (the migration does not run again). */
  resetGuide: () => void;
  /** Shows one guide step now, by id (window.__td.guide.show), for captures and copy checks. */
  showGuideStep: ((id: string) => void) | null;
  currentLevel: () => LevelController | null;
  persist: () => void;
  exportSave: () => string;
  /** Shifts every running timer's end time by the given seconds (negative = sooner). */
  shiftClock: (seconds: number) => void;
  refresh: () => void;
}

/** Dev-only menu (P1.7.5): resources, level jump, clock offset, replay hash, save export. Never built into release UI. */
export function showDebugMenu(parent: HTMLElement, a: DebugActions): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = "sheet panel debug";
  const level = a.currentLevel();
  sheet.innerHTML = `
    <div class="sheethead"><div class="portrait">${icon("gear", 34, "#fff8e6")}</div><div class="titles"><b class="disp">Debug</b><small>dev builds and ?debug only</small></div>${closeBtnHtml()}</div>
    <div class="grid2">
      <button class="btn dark grant">+3000 gold</button>
      <button class="btn dark jump">Unlock all levels</button>
      <button class="btn dark clock">Timers −1 h</button>
      <button class="btn dark hash" ${level ? "" : "disabled"}>Copy replay hash</button>
      <button class="btn dark export">Copy save JSON</button>
      <button class="btn dark speedups">+3 speed-ups</button>
      <button class="btn dark styleguide">Style guide</button>
      <button class="btn dark tutorial">Reset guide</button>
      <button class="btn dark guidestep" ${a.showGuideStep ? "" : "disabled"}>Show step…</button>
      <button class="btn dark skins">Skin switcher</button>
      <button class="btn dark gear">+8 gear</button>
    </div>
    <pre class="out"></pre>`;
  const out = sheet.querySelector<HTMLElement>(".out")!;
  const say = (t: string) => { out.textContent = t; };
  const copy = (text: string) => { try { void navigator.clipboard?.writeText(text); } catch { /* no clipboard */ } say(text); };
  sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
  sheet.querySelector<HTMLButtonElement>(".grant")!.onclick = () => { a.catalog.profile.addGold(3000); a.persist(); a.refresh(); say("granted"); };
  sheet.querySelector<HTMLButtonElement>(".jump")!.onclick = () => { for (let l = 1; l <= a.catalog.levelCount; l++) a.catalog.recordResult(l, true); a.persist(); a.refresh(); say("all levels unlocked"); };
  sheet.querySelector<HTMLButtonElement>(".clock")!.onclick = () => { a.shiftClock(-3600); a.persist(); a.refresh(); say("timers moved 1 h closer"); };
  sheet.querySelector<HTMLButtonElement>(".hash")!.onclick = () => { const l = a.currentLevel(); if (l) copy(`tick ${l.sim.tick} hash ${l.sim.computeHash().toString(16)}`); };
  sheet.querySelector<HTMLButtonElement>(".export")!.onclick = () => copy(a.exportSave());
  sheet.querySelector<HTMLButtonElement>(".speedups")!.onclick = () => { a.catalog.profile.addSpeedUp(5); a.catalog.profile.addSpeedUp(60); a.catalog.profile.addSpeedUp(240); a.persist(); a.refresh(); say("speed-ups added"); };
  sheet.querySelector<HTMLButtonElement>(".styleguide")!.onclick = () => { sheet.remove(); a.styleGuide(); };
  sheet.querySelector<HTMLButtonElement>(".tutorial")!.onclick = () => { a.resetGuide(); say("guide reset — Captain Bram starts over"); };
  sheet.querySelector<HTMLButtonElement>(".guidestep")!.onclick = () => {
    const id = window.prompt("Guide step id (l1.welcome, l1.pickRoad, hint.flyers…)")?.trim();
    if (!id || !a.showGuideStep) return;
    sheet.remove();
    a.showGuideStep(id);
  };
  // Mixed gear for the Armoury: two items of each rarity Common…Epic, rolled for the next level.
  sheet.querySelector<HTMLButtonElement>(".gear")!.onclick = () => { const c = a.catalog; for (let r = Rarity.Common; r <= Rarity.Epic; r++) c.profile.openChestGear(c.nextLevel, r, DEBUG_GEAR_PER_RARITY, BigInt(Date.now() + r)); a.persist(); a.refresh(); say("8 gear items added"); };
  sheet.querySelector<HTMLButtonElement>(".skins")!.onclick = () => { sheet.remove(); SkinSwitcher.open(parent); };
  parent.append(sheet);
  return sheet;
}
